"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useArkose = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const constants_1 = require("./constants");
const ArkoseIframe_module_css_1 = __importDefault(require("./ArkoseIframe.module.css"));
const web_i18n_1 = require("@snapchat/web-i18n");
const web_env_1 = require("@snapchat/web-env");
const web_sentry_1 = require("@snapchat/web-sentry");
const web_graphene_reporting_1 = require("@snapchat/web-graphene-reporting");
const uuid_1 = require("uuid");
const clsx_1 = __importDefault(require("clsx"));
/**
 * Wrapper to integrate with Arkose.
 * Bootstraps and provides an Arkose iframe to be rendered in the UI.
 */
function useArkose(
/**
 * The name of the Arkose key to use.
 */
arkoseKey, isDev, 
/**
 * Runs this on `challenge-complete` event.
 * Challenge could have been suppressed or shown.
 */
onCompleted, 
/**
 * Sizing for the Arkose modal when it's open
 */
challengeDimensions, 
/**
 * Callback when arkose / challenge errors out.
 * Note: We retry once for SRI error
 */
onError, 
/**
 * Callback for when the Arkose challenge UI is hidden.
 */
onCanceled, locale) {
    const [key, setKey] = (0, react_1.useState)((0, uuid_1.v4)());
    const _locale = (0, web_i18n_1.getLocale)(locale);
    const reloadCountRef = (0, react_1.useRef)(0);
    const publicKey = (0, constants_1.arkosePublicKey)(arkoseKey, isDev);
    const arkoseElRef = (0, react_1.createRef)();
    const [challengeVisible, setChallengeVisible] = (0, react_1.useState)(false);
    const resetSession = (0, react_1.useCallback)(() => {
        if (arkoseElRef.current) {
            // Setting key to a different value will reload the iframe.
            setKey((0, uuid_1.v4)());
        }
    }, [arkoseElRef]);
    /**
     * When Arkose wants to suppress a challenge, they do below quickly in succession:
     * - challenge-show > challenge-suppress > challenge-hide
     * Below logic delays showing the challenge a bit, so the UI doesn't flicker.
     */
    const openChallengeTimer = (0, react_1.useRef)();
    const clearOpenChallenge = (0, react_1.useCallback)(() => {
        clearTimeout(openChallengeTimer.current);
        openChallengeTimer.current = undefined;
    }, []);
    const openChallenge = (0, react_1.useCallback)(() => {
        clearOpenChallenge();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        openChallengeTimer.current = (0, web_env_1.getWindow)().setTimeout(() => {
            setChallengeVisible(true);
            openChallengeTimer.current = undefined;
        }, 500);
        return clearOpenChallenge;
    }, [clearOpenChallenge]);
    const closeChallenge = (0, react_1.useCallback)(() => {
        clearOpenChallenge();
        setChallengeVisible(false);
    }, [clearOpenChallenge]);
    const runArkose = () => {
        // Don't try to re-run if the challenge is already visible.
        if (challengeVisible)
            return;
        // The message object to send to the hosted iframe when we want the challenge to open
        const message = {
            publicKey: publicKey,
            eventId: "challenge-open",
        };
        // Sends the message to the hosted iframe
        arkoseElRef.current?.contentWindow?.postMessage(JSON.stringify(message), "*");
        void (0, web_graphene_reporting_1.incrementCounter)("arkose_open_lightbox", 1, {
            arkoseKey,
        });
    };
    // Bootstrap Arkose.
    // Mainly, handle the various events from the Arkose iframe.
    // based on https://developer.arkoselabs.com/docs/iframe-setup-guide#example-implementation
    (0, react_1.useEffect)(() => {
        const arkoseEventListener = (event) => {
            try {
                if (typeof event.data === "object")
                    return;
                const data = JSON.parse(event.data);
                // log all events.
                if (data.eventId) {
                    void (0, web_graphene_reporting_1.incrementCounter)("arkose_event", 1, {
                        arkoseKey,
                        event: data.eventId,
                    });
                }
                // handle some events.
                // https://developer.arkoselabs.com/docs/callbacks
                switch (data.eventId) {
                    case "challenge-error":
                        if (data.payload.error === "SRI_ERROR") {
                            void (0, web_graphene_reporting_1.incrementCounter)("arkose_sri_error", 1, {
                                try_count: String(reloadCountRef.current + 1),
                                locale: _locale,
                            });
                        }
                        if (data.payload.error === "SRI_ERROR" && reloadCountRef.current <= 1) {
                            (0, web_sentry_1.logSentryWarning)(`Reloading Arkose frame due to SRI_ERROR, reload count = ${reloadCountRef.current}`);
                            resetSession();
                            reloadCountRef.current = reloadCountRef.current + 1;
                        }
                        else {
                            const errorMsg = data.payload.error ?? "unknown";
                            void (0, web_graphene_reporting_1.incrementCounter)("arkose_error", 1, {
                                arkoseKey,
                                errorMsg,
                            });
                            onError?.(errorMsg);
                        }
                        break;
                    case "challenge-complete": {
                        const token = data.payload.sessionToken;
                        if (token) {
                            void onCompleted(token);
                        }
                        else {
                            void (0, web_graphene_reporting_1.incrementCounter)("arkose_challenge_complete_no_token");
                            onError?.("No token returned");
                        }
                        reloadCountRef.current = 0;
                        resetSession();
                        break;
                    }
                    case "challenge-show":
                        openChallenge();
                        break;
                    case "challenge-hide":
                        closeChallenge();
                        onCanceled?.();
                        break;
                    default:
                }
            }
            catch (error) {
                const errMsg = error.message;
                (0, web_sentry_1.logSentryError)("failed to handle Arkose event", error);
                void (0, web_graphene_reporting_1.incrementCounter)("arkose_handle_event_error", 1, {
                    arkoseKey,
                    errMsg,
                });
            }
        };
        (0, web_env_1.getWindow)()?.addEventListener("message", arkoseEventListener);
        return () => {
            (0, web_env_1.getWindow)()?.removeEventListener("message", arkoseEventListener);
        };
    }, [
        arkoseKey,
        arkoseElRef,
        onCanceled,
        _locale,
        resetSession,
        onError,
        onCompleted,
        openChallenge,
        closeChallenge,
    ]);
    (0, react_1.useEffect)(() => {
        // Arkose can get stale when sitting for a while.
        // We need to continually reset the session to keep it fresh.
        // Reset arkose every 20m.
        const resetArkoseInterval = setInterval(resetSession, 20 * 60 * 1000);
        return () => {
            clearInterval(resetArkoseInterval);
        };
    }, [resetSession]);
    return {
        // the arkose element, should be rendered in your html
        arkoseEl: ((0, jsx_runtime_1.jsx)("div", { className: (0, clsx_1.default)(ArkoseIframe_module_css_1.default.arkoseIframeContainer, [challengeVisible ? ArkoseIframe_module_css_1.default.open : ""]), style: challengeVisible ? { ...challengeDimensions, minHeight: challengeDimensions.height } : {}, children: (0, jsx_runtime_1.jsx)("iframe", { ref: arkoseElRef, className: ArkoseIframe_module_css_1.default.arkoseIframeLightbox, src: "https://iframe.arkoselabs.com/" + publicKey + "/lightbox" + ".html?mkt=" + _locale }) }, key)),
        // reset arkose session - this automatically happens on challenge complete
        resetSession,
        // run arkose challenge, called when you need an Arkose token to perform an action
        runArkose,
        // Arkose challenge is visible.
        challengeVisible,
        // Close the challenge UI.
        closeChallenge,
    };
}
exports.useArkose = useArkose;
//# sourceMappingURL=index.js.map